// src/careComponents/components/chatPane.tsx
import * as React from "react";
import {
  Box,
  Button,
  Paper,
  Stack,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  MenuItem,
  Select,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import {
  ApolloCache,
  useMutation,
  useQuery,
  useSubscription,
} from "@apollo/client";
import { CHAT_MESSAGES } from "../../graphql/queries/care";
import {
  SEND_CHAT_MESSAGE,
  CHAT_MESSAGE_ADDED,
} from "../../graphql/mutations/care";

type ChatChannelClient = "PROVIDER" | "REFERRER";
type ViewerRole = "AGENT" | "PROVIDER" | "REFERRER";

type ChatMessage = {
  __typename?: "ChatMessage";
  id: string;
  leadId: string;
  channel: ChatChannelClient;
  senderRole: ViewerRole;
  body: string;
  createdAt: string | number | Date;
};

type Props = {
  leadId: string | number;
  audience?: ChatChannelClient | "MERGED";
  viewerRole?: ViewerRole;
  title?: string;
  height?: number | string;
  limit?: number;
  pollMs?: number;
  disableSubscription?: boolean;
  allowMergedSendTargetSelect?: boolean;
};

function formatDateTime(input: unknown): string {
  if (!input) return "—";
  if (input instanceof Date && !isNaN(input.valueOf()))
    return input.toLocaleString();
  if (typeof input === "number" && Number.isFinite(input)) {
    const ms = input < 1e12 ? input * 1000 : input;
    const d = new Date(ms);
    return isNaN(d.valueOf()) ? "—" : d.toLocaleString();
  }
  if (typeof input === "string") {
    const s = input.trim();
    if (/^\d+$/.test(s)) {
      const n = Number(s);
      const ms = s.length === 10 ? n * 1000 : n;
      const d = new Date(ms);
      return isNaN(d.valueOf()) ? "—" : d.toLocaleString();
    }
    const isoish =
      s.includes(" ") && !s.includes("T") ? s.replace(" ", "T") : s;
    const d = new Date(isoish);
    return isNaN(d.valueOf()) ? "—" : d.toLocaleString();
  }
  return "—";
}

function appendToCache(
  cache: ApolloCache<unknown>,
  vars: {
    leadId: string;
    channel: ChatChannelClient;
    viewerRole?: ViewerRole; // include in vars so it matches the query signature
    limit: number;
  },
  msg: ChatMessage
) {
  if (!msg?.id) return;
  const prev = cache.readQuery<{ chatMessages: ChatMessage[] }>({
    query: CHAT_MESSAGES,
    variables: vars,
  });
  const arr = prev?.chatMessages ?? [];
  if (arr.some((m) => m.id === msg.id)) return;
  cache.writeQuery({
    query: CHAT_MESSAGES,
    variables: vars,
    data: { chatMessages: [...arr, msg] },
  });
}

export default function ChatPane({
  leadId,
  audience = "PROVIDER",
  viewerRole = "AGENT",
  title,
  height = 520,
  limit = 200,
  pollMs = 0,
  disableSubscription = false,
  allowMergedSendTargetSelect = true,
}: Props): JSX.Element {
  const lead = String(leadId);
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const lastMessageIdRef = React.useRef<string | null>(null);
  const [isNearBottom, setIsNearBottom] = React.useState(true);
  const [unseenCount, setUnseenCount] = React.useState(0);

  const scrollToBottom = React.useCallback((behavior: ScrollBehavior = "smooth") => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
  }, []);

  const computeNearBottom = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return true;
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    return remaining < 72;
  }, []);

  // ---- Queries (pass viewerRole) ------------------------------------------
  const providerQ = useQuery(CHAT_MESSAGES, {
    variables: {
      leadId: lead,
      channel: "PROVIDER" as ChatChannelClient,
      viewerRole,
      limit,
    },
    fetchPolicy: "cache-and-network",
    pollInterval: pollMs || 0,
    skip: audience !== "MERGED" && audience !== "PROVIDER",
  });

  const referrerQ = useQuery(CHAT_MESSAGES, {
    variables: {
      leadId: lead,
      channel: "REFERRER" as ChatChannelClient,
      viewerRole,
      limit,
    },
    fetchPolicy: "cache-and-network",
    pollInterval: pollMs || 0,
    skip: audience !== "MERGED" && audience !== "REFERRER",
  });

  const loading =
    (audience === "PROVIDER" && providerQ.loading) ||
    (audience === "REFERRER" && referrerQ.loading) ||
    (audience === "MERGED" && (providerQ.loading || referrerQ.loading));

  // ---- Subscriptions -------------------------------------------------------
  useSubscription(CHAT_MESSAGE_ADDED, {
    skip:
      disableSubscription || (audience !== "MERGED" && audience !== "PROVIDER"),
    variables: { leadId: lead, channel: "PROVIDER" as ChatChannelClient },
    onData: ({ client, data }) => {
      const msg = data.data?.chatMessageAdded as ChatMessage | undefined;
      if (!msg) return;
      appendToCache(
        client.cache,
        { leadId: lead, channel: "PROVIDER", viewerRole, limit },
        msg
      );
    },
  });

  useSubscription(CHAT_MESSAGE_ADDED, {
    skip:
      disableSubscription || (audience !== "MERGED" && audience !== "REFERRER"),
    variables: { leadId: lead, channel: "REFERRER" as ChatChannelClient },
    onData: ({ client, data }) => {
      const msg = data.data?.chatMessageAdded as ChatMessage | undefined;
      if (!msg) return;
      appendToCache(
        client.cache,
        { leadId: lead, channel: "REFERRER", viewerRole, limit },
        msg
      );
    },
  });

  // ---- Timeline ------------------------------------------------------------
  const providerMsgs = (providerQ.data?.chatMessages ?? []) as ChatMessage[];
  const referrerMsgs = (referrerQ.data?.chatMessages ?? []) as ChatMessage[];

  const messages: ChatMessage[] = React.useMemo(() => {
    const base =
      audience === "MERGED"
        ? [...providerMsgs, ...referrerMsgs]
        : audience === "PROVIDER"
        ? providerMsgs
        : referrerMsgs;

    return [...base].sort(
      (a, b) =>
        new Date(a.createdAt as any).getTime() -
        new Date(b.createdAt as any).getTime()
    );
  }, [audience, providerMsgs, referrerMsgs]);

  React.useEffect(() => {
    if (!messages.length) return;

    const latest = messages[messages.length - 1];
    const latestId = String(latest.id);
    const previousId = lastMessageIdRef.current;
    const shouldStickToBottom =
      previousId == null || isNearBottom || latest.senderRole === viewerRole;

    if (shouldStickToBottom) {
      setUnseenCount(0);
      window.requestAnimationFrame(() => scrollToBottom(previousId == null ? "auto" : "smooth"));
    } else if (latestId !== previousId) {
      setUnseenCount((count) => count + 1);
    }

    lastMessageIdRef.current = latestId;
  }, [messages, isNearBottom, scrollToBottom, viewerRole]);

  const handleScroll = React.useCallback(() => {
    const nearBottom = computeNearBottom();
    setIsNearBottom(nearBottom);
    if (nearBottom) {
      setUnseenCount(0);
    }
  }, [computeNearBottom]);

  // ---- Send ---------------------------------------------------------------
  const [sendTo, setSendTo] = React.useState<ChatChannelClient>("PROVIDER");
  React.useEffect(() => {
    if (audience === "PROVIDER") setSendTo("PROVIDER");
    if (audience === "REFERRER") setSendTo("REFERRER");
  }, [audience]);

  const [text, setText] = React.useState("");
  const [send] = useMutation(SEND_CHAT_MESSAGE);

  const onSend = async () => {
    const body = text.trim();
    if (!body) return;
    const channel: ChatChannelClient =
      audience === "MERGED" ? sendTo : (audience as ChatChannelClient);
    setText("");

    const optimisticRow: ChatMessage = {
      __typename: "ChatMessage",
      id: `optimistic-${channel}-${Date.now()}`,
      leadId: lead,
      channel,
      senderRole: viewerRole, // align immediately to "me"
      body,
      createdAt: new Date().toISOString(),
    };

    await send({
      variables: {
        input: {
          leadId: lead,
          channel,
          senderRole: viewerRole, // <-- server expects senderRole
          body,
          meta: null,
        },
      },
      optimisticResponse: {
        sendChatMessage: {
          __typename: "SendChatMessageResult",
          success: true,
          message: "ok",
          row: optimisticRow,
        },
      },
      update: (cache, { data: resp }) => {
        const newMsg = (resp?.sendChatMessage?.row ??
          optimisticRow) as ChatMessage;
        appendToCache(
          cache,
          { leadId: lead, channel, viewerRole, limit },
          newMsg
        );
      },
    }).catch(() => setText(body));
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  const headerTitle =
    title ??
    (audience === "PROVIDER"
      ? "Agent ↔ Provider"
      : audience === "REFERRER"
      ? "Agent ↔ Referrer"
      : "Care Team (Provider + Referrer)");

  // ---- UI ------------------------------------------------------------------
  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.25, height, display: "flex", flexDirection: "column" }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Typography variant="subtitle1" fontWeight={700}>
          {headerTitle}
        </Typography>
        {loading ? <CircularProgress size={18} /> : null}
      </Stack>
      <Divider sx={{ mb: 1 }} />

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mb: 1, px: 0.5 }}
      >
        Conversation history
      </Typography>

      {/* RIGHT for me (senderRole === viewerRole), LEFT for others */}
      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        sx={{ flex: 1, overflowY: "auto", pr: 1 }}
      >
        <List disablePadding>
          {messages.length ? (
            messages.map((m) => {
              const mine = m.senderRole === viewerRole;
              const showChannelTag = audience === "MERGED";
              const laneTag = m.channel === "PROVIDER" ? "Provider" : "Referrer";
              const who =
                m.senderRole === "AGENT"
                  ? "Agent"
                  : m.senderRole === "PROVIDER"
                  ? "Provider"
                  : "Referrer";

              return (
                <ListItem
                  key={m.id}
                  disableGutters
                  sx={{ justifyContent: mine ? "flex-end" : "flex-start" }}
                >
                  <Box
                    sx={{
                      maxWidth: "80%",
                      bgcolor: mine ? "primary.main" : "action.hover",
                      color: mine ? "primary.contrastText" : "text.primary",
                      px: 1.25,
                      py: 0.75,
                      borderRadius: 2,
                    }}
                  >
                    <Stack direction="row" spacing={1} sx={{ mb: 0.25 }}>
                      {mine ? (
                        <Chip size="small" variant="outlined" label="You" />
                      ) : (
                        <Chip size="small" variant="outlined" label={who} />
                      )}
                      {showChannelTag && (
                        <Chip size="small" variant="outlined" label={laneTag} />
                      )}
                    </Stack>

                    <ListItemText
                      primary={
                        <span style={{ whiteSpace: "pre-wrap" }}>{m.body}</span>
                      }
                      secondary={
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                          {formatDateTime(m.createdAt)}
                        </Typography>
                      }
                    />
                  </Box>
                </ListItem>
              );
            })
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", mt: 2 }}
            >
              No messages yet.
            </Typography>
          )}
        </List>
      </Box>

      {unseenCount > 0 && !isNearBottom ? (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1 }}>
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              setUnseenCount(0);
              setIsNearBottom(true);
              scrollToBottom("smooth");
            }}
          >
            {unseenCount === 1 ? "1 new message" : `${unseenCount} new messages`}
          </Button>
        </Box>
      ) : null}

      {/* Composer */}
      <Stack direction="row" spacing={1} sx={{ pt: 1, alignItems: "center" }}>
        {audience === "MERGED" && allowMergedSendTargetSelect && (
          <Select
            size="small"
            value={sendTo}
            onChange={(e) =>
              setSendTo(e.target.value as ChatChannelClient)
            }
            sx={{ minWidth: 130 }}
          >
            <MenuItem value="PROVIDER">To: Provider</MenuItem>
            <MenuItem value="REFERRER">To: Referrer</MenuItem>
          </Select>
        )}

        <TextField
          size="small"
          fullWidth
          multiline
          minRows={1}
          maxRows={4}
          placeholder="Type a message…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
        />
        <IconButton color="primary" onClick={onSend} disabled={!text.trim()}>
          <SendIcon />
        </IconButton>
      </Stack>
    </Paper>
  );
}
